import axios from 'axios';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';
import { clearClientSession } from '../auth/sessionCleanup';
import { getPwaInstallationId } from '../utils/installation';

/**
 * Cliente HTTP Axios configurado para a API do Aluno Conectado.
 *
 * @remarks
 * - BaseURL: `VITE_API_URL` ou `/api/v1`
 * - Interceptor de request: usa cookie web por padrao e Bearer so em superficies controladas
 * - Interceptor de response: redireciona para `/login` em caso de 401
 *
 * @example
 * ```ts
 * import { api } from './api/client';
 * const { data } = await api.get('/escolas/123/alunos');
 * ```
 *
 * @category API
 */
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  // httpOnly cookies são enviados automaticamente com withCredentials
  withCredentials: true,
});

/** Mensagem amigável de erro da API (StructuredExceptionFilter ou ValidationPipe). */
export function extractApiErrorMessage(err: unknown, fallback = 'Erro na operação'): string {
  const resp = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (!resp) return fallback;
  const structured = resp.error as { message?: string } | undefined;
  if (typeof structured?.message === 'string') return structured.message;
  const raw = resp.message;
  if (Array.isArray(raw)) return raw.join('. ');
  if (typeof raw === 'string') return raw;
  return fallback;
}

const CSRF_STORAGE_KEY = 'ac_csrf_token';

/** Helper: lê cookie pelo nome (para CSRF token) */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

/** Persiste CSRF legível pelo JS (fallback quando cookie cross-subdomain falha). */
export function persistCsrfToken(token: string | null | undefined): void {
  if (!token || typeof token !== 'string') return;
  try {
    sessionStorage.setItem(CSRF_STORAGE_KEY, token);
  } catch {
    // noop
  }
}

function getCsrfToken(): string | null {
  const fromCookie = getCookie('ac_csrf_token');
  if (fromCookie) {
    persistCsrfToken(fromCookie);
    return fromCookie;
  }
  try {
    return sessionStorage.getItem(CSRF_STORAGE_KEY);
  } catch {
    return null;
  }
}

let csrfEnsurePromise: Promise<string | null> | null = null;

/** Garante CSRF válido (cookie + sessionStorage). Seguro chamar no boot. */
export async function ensureCsrfToken(opts?: { force?: boolean }): Promise<string | null> {
  if (!opts?.force) {
    const existing = getCsrfToken();
    if (existing) return existing;
  } else {
    try {
      sessionStorage.removeItem(CSRF_STORAGE_KEY);
      if (typeof window !== 'undefined' && 'caches' in window) {
        caches.keys().then((keys) => {
          keys.forEach((key) => {
            caches.open(key).then((cache) => {
              cache.keys().then((requests) => {
                requests.forEach((req) => {
                  if (req.url.includes('/auth/csrf-token')) {
                    cache.delete(req).catch(() => {});
                  }
                });
              });
            });
          });
        });
      }
    } catch {
      /* noop */
    }
    csrfEnsurePromise = null;
  }
  if (!csrfEnsurePromise) {
    csrfEnsurePromise = axios
      .get(`${api.defaults.baseURL}/auth/csrf-token?t=${Date.now()}`, { withCredentials: true })
      .then((res) => {
        const token = res.data?.csrfToken as string | undefined;
        persistCsrfToken(token);
        return token || getCsrfToken();
      })
      .catch(() => getCsrfToken())
      .finally(() => {
        csrfEnsurePromise = null;
      });
  }
  return csrfEnsurePromise;
}

/**
 * fetch com CSRF + credentials (cockpit Chronos / raw fetch fora do Axios).
 * Em CSRF_INVALID renova o token e tenta 1 vez.
 */
export async function apiFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const method = (init.method || 'GET').toUpperCase();
  const isMutation = ['POST', 'PATCH', 'PUT', 'DELETE'].includes(method);

  const run = async (forceCsrf: boolean) => {
    const headers = new Headers(init.headers || {});
    if (isMutation) {
      const csrf = await ensureCsrfToken(forceCsrf ? { force: true } : undefined);
      if (csrf) headers.set('X-CSRF-Token', csrf);
    }
    return fetch(input, {
      ...init,
      headers,
      credentials: init.credentials ?? 'include',
    });
  };

  const res = await run(false);
  if (!isMutation || res.status !== 403) return res;

  const data = await res.clone().json().catch(() => ({} as any));
  const csrfBad =
    data?.code === 'CSRF_INVALID' ||
    data?.error?.code === 'CSRF_INVALID' ||
    data?.message === 'CSRF token invalido ou ausente' ||
    data?.error?.message === 'CSRF token invalido ou ausente';
  if (!csrfBad) return res;
  return run(true);
}

function isCsrfInvalidError(error: any): boolean {
  const data = error?.response?.data;
  return (
    error?.response?.status === 403 &&
    (data?.code === 'CSRF_INVALID' ||
      data?.error?.code === 'CSRF_INVALID' ||
      data?.message === 'CSRF token invalido ou ausente' ||
      data?.error?.message === 'CSRF token invalido ou ausente')
  );
}

function isImpersonatedSession(): boolean {
  try {
    return (
      sessionStorage.getItem('ac_impersonate') === 'true' ||
      window.location.search.includes('impersonate_receiver=true')
    );
  } catch {
    return false;
  }
}

function isDemoSession(): boolean {
  try {
    return localStorage.getItem('ac_demo_mode') === 'true';
  } catch {
    return false;
  }
}

function isPwaStandalone(): boolean {
  try {
    return window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
  } catch {
    return false;
  }
}

function normalizeApiPath(url?: string): string {
  if (!url) return '';
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname.replace(/^\/api\/v1/, '') || '/';
  } catch {
    return url.replace(/^\/api\/v1/, '');
  }
}

type AcRequestConfig = {
  url?: string;
  headers?: any;
  _acUseBearer?: boolean;
  _acBearerToken?: string;
};

function getExplicitBearerToken(config?: AcRequestConfig): string | null {
  const token = config?._acBearerToken?.trim();
  return token || null;
}

function stripInternalBearerHeader(config?: AcRequestConfig): boolean {
  const headers = config?.headers as any;
  const explicitBearer = headers?.['X-AC-Use-Bearer'] === 'true' || headers?.['X-AC-Use-Bearer'] === true;
  if (headers?.['X-AC-Use-Bearer'] !== undefined) {
    delete headers['X-AC-Use-Bearer'];
  }
  return explicitBearer;
}

function shouldAttachBearer(config?: AcRequestConfig): boolean {
  const explicitHeaderBearer = stripInternalBearerHeader(config);
  if (getExplicitBearerToken(config)) {
    if (config) config._acUseBearer = true;
    return true;
  }
  if (config?._acUseBearer === true) return true;

  if (explicitHeaderBearer || isImpersonatedSession() || isDemoSession()) {
    if (config) config._acUseBearer = true;
    return true;
  }

  const path = normalizeApiPath(config?.url);
  if (path === '/auth/login') return false;

  // Para qualquer outra rota interna, anexa o Bearer token para suportar ngrok/túneis de desenvolvimento
  if (config) {
    config._acUseBearer = true;
    return true;
  }
  return false;
}

function getBearerToken(config?: AcRequestConfig): string | null {
  const explicitToken = getExplicitBearerToken(config);
  if (explicitToken) {
    stripInternalBearerHeader(config);
    return explicitToken;
  }
  if (!shouldAttachBearer(config)) return null;

  try {
    if (isImpersonatedSession()) {
      return sessionStorage.getItem('ac_token');
    }
    return localStorage.getItem('ac_token');
  } catch {
    return null;
  }
}

function getAuthSurface(config?: AcRequestConfig): string {
  if (getExplicitBearerToken(config)) return 'explicit-bearer';
  if (isImpersonatedSession()) return 'impersonation';
  if (isDemoSession()) return 'demo';
  return 'mobile-pwa';
}

function getRefreshToken(): string | null {
  try {
    if (isImpersonatedSession()) {
      return sessionStorage.getItem('ac_refresh_token');
    }
    return localStorage.getItem('ac_refresh_token');
  } catch {
    return null;
  }
}

function shouldRedirectAfterAuthFailure(originalRequest: any): boolean {
  const publicPrefixes = ['/ativar', '/login', '/convite', '/security', '/download', '/demo', '/tv', '/install', '/s/', '/simulador', '/sim'];
  const isPublicPage = publicPrefixes.some((p) => window.location.pathname.startsWith(p))
    || window.location.pathname === '/';
  const isTrackingCall = originalRequest?.url?.includes('/observe/');
  const isSessionCheck = originalRequest?.url?.includes('/auth/me');

  return !isPublicPage && !isTrackingCall && !isSessionCheck;
}

function shouldSkipAuthRefreshFor401(originalRequest: any): boolean {
  const path = normalizeApiPath(originalRequest?.url);
  return path === '/auth/login';
}

/**
 * Interceptor de request:
 * 1. Injeta Authorization header apenas para impersonacao, demo e mobile/PWA
 *    O cookie httpOnly é enviado automaticamente via withCredentials
 * 2. Injeta X-CSRF-Token em mutations (POST/PATCH/DELETE/PUT)
 */
api.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};

  try {
    (config.headers as any)['X-AC-Fingerprint'] = getDeviceFingerprint();
    
    // Injetar ID de instalacao unico do PWA
    (config.headers as any)['X-AC-Installation-Id'] = getPwaInstallationId();

    // UUID estável do dispositivo (não muda com IP, user agent, etc.)
    let deviceUuid: string | null = null;
    try {
      deviceUuid = localStorage.getItem('ac_device_id');
    } catch {
      // noop
    }
    if (deviceUuid) {
      (config.headers as any)['X-AC-Device-UUID'] = deviceUuid;
    }
    
    // Identificar PWA standalone
    const isPwa = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
    if (isPwa) {
      (config.headers as any)['X-AC-PWA'] = 'true';
    }
  } catch {
    // noop
  }
  // Web normal usa cookie httpOnly. Bearer fica restrito a superficies controladas.
  const token = getBearerToken(config);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    (config.headers as any)['X-AC-Auth-Surface'] = getAuthSurface(config as AcRequestConfig);
  } else {
    delete (config.headers as any).Authorization;
    delete (config.headers as any)['X-AC-Auth-Surface'];
  }

  // CSRF protection: enviar token em mutations (cookie OU sessionStorage)
  const method = (config.method || '').toUpperCase();
  if (['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }

  return config;
});

const isSessionReplaced = (err: any): boolean => {
  const data = err?.response?.data;
  return data?.code === 'SESSION_REPLACED' || data?.error?.code === 'SESSION_REPLACED';
};

/** Interceptor: refresh silencioso em 401 (token expirado) */
let isRefreshing = false;
let failedQueue: Array<{ resolve: (v: any) => void; reject: (e: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  async (response) => {
    const config = response.config;
    if (response.data?.csrfToken) {
      persistCsrfToken(response.data.csrfToken);
    }
    // Se for uma requisição GET bem-sucedida, não-auth e não-bypass, grava no cache local
    if (config.method?.toLowerCase() === 'get' && config.url && !config.url.includes('/auth/') && !config.headers?.['X-AC-Bypass-Cache']) {
      try {
        const { cacheGetResponse } = await import('../lib/offline/syncQueue');
        await cacheGetResponse(config.url, response.data);
      } catch (e) {
        console.warn('[OfflineCache] Falha ao gravar cache GET:', e);
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest) return Promise.reject(error);

    // Emite evento global de telemetria para session-recorder e logs de diagnóstico
    try {
      if (typeof window !== 'undefined') {
        const isNetworkErr = !error.response || error.code === 'ERR_NETWORK' || !navigator.onLine;
        const status = error.response?.status || (isNetworkErr ? 'OFFLINE' : 'ERR');
        const url = normalizeApiPath(originalRequest.url);
        // Evita flood de telemetria em polling de versão e rotas internas de observe
        if (!url.includes('/version-manifest') && !url.includes('/auth/csrf-token')) {
          const message = extractApiErrorMessage(error, error.message || 'Falha na requisição');
          window.dispatchEvent(
            new CustomEvent('ac:client-error', {
              detail: {
                status,
                url,
                method: (originalRequest.method || 'GET').toUpperCase(),
                message: `[${(originalRequest.method || 'GET').toUpperCase()} ${url}] (${status}): ${message}`,
                timestamp: Date.now(),
              },
            })
          );
        }
      }
    } catch {
      // noop
    }

    // CSRF inválido: renova token e tenta 1 vez (resolve apagar-escola / push/register etc.)
    if (isCsrfInvalidError(error) && !(originalRequest as any)._csrfRetry) {
      (originalRequest as any)._csrfRetry = true;
      try {
        sessionStorage.removeItem(CSRF_STORAGE_KEY);
      } catch { /* noop */ }
      const fresh = await ensureCsrfToken({ force: true });
      if (fresh) {
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers['X-CSRF-Token'] = fresh;
        return api.request(originalRequest);
      }
    }

    const isNetworkError = !error.response || error.code === 'ERR_NETWORK' || !navigator.onLine;

    // ── TRATAMENTO OFFLINE GENÉRICO ──
    if (isNetworkError) {
      const method = (originalRequest.method || '').toLowerCase();

      if (method === 'get') {
        try {
          const { getCachedGetResponse } = await import('../lib/offline/syncQueue');
          const cached = await getCachedGetResponse(originalRequest.url);
          if (cached) {
            console.log(`[OfflineCache] Servindo dados de cache offline para: ${originalRequest.url}`);
            return {
              data: cached,
              status: 200,
              statusText: 'OK (Offline Cache)',
              headers: {},
              config: originalRequest,
            } as any;
          }
        } catch (e) {
          console.error('[OfflineCache] Erro ao ler cache offline:', e);
        }
      } else if (['post', 'put', 'patch', 'delete'].includes(method)) {
        const isAuth = originalRequest.url?.includes('/auth/');
        const bypassOutbox = originalRequest.headers?.['X-AC-Bypass-Outbox'];

        if (!isAuth && !bypassOutbox) {
          try {
            const { queueWriteRequest } = await import('../lib/offline/syncQueue');
            await queueWriteRequest({
              url: originalRequest.url,
              method: originalRequest.method,
              data: originalRequest.data,
              headers: { ...originalRequest.headers },
            });
            console.log(`[OfflineOutbox] Operação enfileirada no outbox offline: ${originalRequest.method} ${originalRequest.url}`);

            try {
              const { toast } = await import('sonner');
              toast.info('Você está offline. Suas alterações foram salvas no dispositivo e serão enviadas automaticamente assim que a conexão retornar.', {
                duration: 6000,
              });
            } catch { /* noop */ }

            return {
              data: { ok: true, _queued: true },
              status: 200,
              statusText: 'OK (Queued Offline)',
              headers: {},
              config: originalRequest,
            } as any;
          } catch (e) {
            console.error('[OfflineOutbox] Erro ao enfileirar operação:', e);
          }
        }
      }

      // Se for erro de rede e não conseguimos tratar (ou não tinha cache), rejeita
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && getExplicitBearerToken(originalRequest)) {
      return Promise.reject(error);
    }

    // Detect if we had a session token and got a 401
    const hadToken = !!(localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token'));
    const isReplaced = isSessionReplaced(error);
    
    if (error.response?.status === 401 && hadToken) {
      const reason = isReplaced ? 'replaced' : 'expired';
      try {
        sessionStorage.setItem('ac_logout_reason', reason);
        localStorage.setItem('ac_logout_reason', reason);
      } catch { /* ignore */ }
    }

    // Se não é 401 ou já tentou refresh, rejeita
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Se é request de refresh que falhou, desloga
    if (shouldSkipAuthRefreshFor401(originalRequest)) {
      return Promise.reject(error);
    }

    if (originalRequest.url?.includes('/auth/refresh')) {
      console.error('[Interceptor] ❌ REFRESH FALHOU — redirecionando!', originalRequest.url);
      const isRefreshReplaced = isSessionReplaced(error);
      await clearClientSession();
      const reason = isRefreshReplaced ? 'replaced' : 'expired';
      try {
        sessionStorage.setItem('ac_logout_reason', reason);
        localStorage.setItem('ac_logout_reason', reason);
      } catch { /* ignore */ }
      if (shouldRedirectAfterAuthFailure(originalRequest)) {
        window.location.href = isRefreshReplaced ? '/login?replaced=true' : '/login?expired=true';
      }
      return Promise.reject(error);
    }

    // Se a sessão foi substituída, não adianta tentar refresh
    if (isReplaced) {
      console.warn('[Interceptor] ❌ Sessão substituída por outro login. Redirecionando...');
      await clearClientSession();
      try {
        sessionStorage.setItem('ac_logout_reason', 'replaced');
        localStorage.setItem('ac_logout_reason', 'replaced');
        const details = error.response?.data?.details;
        if (details) {
          sessionStorage.setItem('ac_logout_details', JSON.stringify(details));
          localStorage.setItem('ac_logout_details', JSON.stringify(details));
        }
      } catch { /* ignore */ }
      window.location.href = '/login?replaced=true';
      return Promise.reject(error);
    }

    // Se já está renovando, enfileira
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers = originalRequest.headers ?? {};
        if (token && shouldAttachBearer(originalRequest)) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        } else {
          delete originalRequest.headers.Authorization;
        }
        return api.request(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = getRefreshToken();
    const retryNeedsBearer = shouldAttachBearer(originalRequest);

    if (!refreshToken && retryNeedsBearer) {
      const publicPrefixes = ['/ativar', '/login', '/convite', '/security', '/download', '/demo', '/tv', '/install', '/s/', '/simulador', '/sim'];

      const isPublicPage = publicPrefixes.some(p => window.location.pathname.startsWith(p))
        || window.location.pathname === '/';
      const isTrackingCall = originalRequest.url?.includes('/observe/');
      const isSessionCheck = originalRequest.url?.includes('/auth/me');

      console.warn('[Interceptor 401]', {
        url: originalRequest.url,
        pathname: window.location.pathname,
        isPublicPage,
        isTrackingCall,
        isSessionCheck,
        willRedirect: !isPublicPage && !isTrackingCall && !isSessionCheck,
      });

      if (!isPublicPage && !isTrackingCall && !isSessionCheck) {
        console.error('[Interceptor] ❌ REDIRECIONANDO para /login!');
        await clearClientSession();
        try {
          sessionStorage.setItem('ac_logout_reason', 'expired');
          localStorage.setItem('ac_logout_reason', 'expired');
        } catch { /* ignore */ }
        window.location.href = '/login?expired=true';
      }
      isRefreshing = false;
      return Promise.reject(error);
    }

    try {
      const { data } = await axios.post(
        `${api.defaults.baseURL}/auth/refresh`,
        refreshToken ? { refresh_token: refreshToken } : {},
        { withCredentials: true },
      );
      if (data?.csrfToken) persistCsrfToken(data.csrfToken);
      const newToken = data.access_token;
      if (retryNeedsBearer && newToken) {
        if (isImpersonatedSession()) {
          sessionStorage.setItem('ac_token', newToken);
          if (data.refresh_token) sessionStorage.setItem('ac_refresh_token', data.refresh_token);
        } else {
          localStorage.setItem('ac_token', newToken);
          if (data.refresh_token) localStorage.setItem('ac_refresh_token', data.refresh_token);
        }
      }
      processQueue(null, retryNeedsBearer ? newToken : null);
      originalRequest.headers = originalRequest.headers ?? {};
      if (retryNeedsBearer && newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
      } else {
        delete originalRequest.headers.Authorization;
      }
      return api.request(originalRequest);
    } catch (refreshError: any) {
      processQueue(refreshError, null);
      const isRefreshReplaced = isSessionReplaced(refreshError);
      await clearClientSession();
      const reason = isRefreshReplaced ? 'replaced' : 'expired';
      try {
        sessionStorage.setItem('ac_logout_reason', reason);
        localStorage.setItem('ac_logout_reason', reason);
      } catch { /* ignore */ }
      if (shouldRedirectAfterAuthFailure(originalRequest)) {
        window.location.href = isRefreshReplaced ? '/login?replaced=true' : '/login?expired=true';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);


